<template>
  <div>
    <div class="air__utils__heading">
      <div class="row">
        <div class="col-6">
          <h5>Hostel Daily Attendance</h5>
        </div>
      </div>
    </div>

    <div class="row justify-content-center">
      <div class="col-xl-12">
        <!-- Filters -->
        <div class="row mb-3">
          <!-- Hostel Filter -->
          <div class="col-md-3 col-12 mb-2">
            <a-select
              size="large"
              v-model="selectedHostel"
              placeholder="Filter by Hostel"
              style="width: 100%"
              allowClear
              @change="handleHostelChange"
            >
              <a-select-option v-for="h in hostels" :key="h.id" :value="h.id">
                {{ h.name }}
              </a-select-option>
            </a-select>
          </div>
          <!-- Event -->
          <div class="col-md-3 col-12 mb-2">
            <a-select
              size="large"
              v-model="selectedEventId"
              placeholder="Select Event"
              style="width: 100%"
              @change="handleEventChange"
            >
              <a-select-option v-for="event in filteredEvents" :key="event.id" :value="event.id">
                {{ event.name }}
                <small class="text-muted" v-if="event.hostel_id">({{ getHostelName(event.hostel_id) }})</small>
                <small class="text-muted" v-else>(Global)</small>
              </a-select-option>
            </a-select>
          </div>
          <!-- Date -->
          <div class="col-md-3 col-12 mb-2">
            <a-date-picker
              size="large"
              style="width: 100%"
              v-model="selectedDate"
              :format="dateFormat"
              :disabled-date="disabledDate"
              @change="handleDateChange"
            />
          </div>
        </div>

        <!-- Attendance Table Component -->
        <div class="card">
          <div class="card-body">
            <hostel-attendance-table
              v-if="attendanceData"
              :loading="loading"
              :data="attendanceData.data || []"
              :eventInfo="attendanceData.hostel_event"
              :timing="attendanceData.timing"
              :date="attendanceData.date"
              :summary="attendanceSummary"
              :filterStatus="filterStatus"
              :approveLoading="approveLoading"
              @filter-change="handleFilterChange"
              @approve="handleApprove"
            />
            <div v-else-if="!loading" class="text-center py-5">
              <a-empty description="Select an event and date to view attendance" />
            </div>
            <a-spin v-else />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import HostelAttendanceTable from '@/components/hostel/manage/hostel-attendance/Table'

export default {
  name: 'HostelDailyAttendanceView',
  components: {
    HostelAttendanceTable,
  },
  data() {
    return {
      loading: false,
      approveLoading: false,
      dateFormat: 'DD-MM-YYYY',

      hostels: [],
      events: [],
      selectedHostel: undefined,
      selectedEventId: undefined,
      selectedDate: moment(),
      filterStatus: 'all',

      attendanceData: null,
    }
  },
  computed: {
    filteredEvents() {
      if (!this.selectedHostel) {
        return this.events
      }
      return this.events.filter(e => e.hostel_id === null || e.hostel_id === this.selectedHostel)
    },
    attendanceSummary() {
      if (this.attendanceData && this.attendanceData.summary) {
        return this.attendanceData.summary
      }
      return { total: 0, present: 0, absent: 0, pending: 0 }
    },
  },
  mounted() {
    this.fetchInitialData()
  },
  methods: {
    moment,
    disabledDate(current) {
      // Can not select days after today
      return current && current > moment().endOf('day')
    },
    fetchInitialData() {
      Promise.all([
        this.$http.get('/api/hostel'),
        this.$http.get('/api/hostel/event/all'),
      ])
        .then(([hostelsRes, eventsRes]) => {
          this.hostels = hostelsRes.data.data
          this.events = eventsRes.data.data || eventsRes.data
        })
    },
    getHostelName(hostelId) {
      const h = this.hostels.find(h => h.id === hostelId)
      return h ? h.name : ''
    },
    handleHostelChange(value) {
      this.selectedHostel = value || undefined
      if (this.selectedEventId) {
        const eventStillValid = this.filteredEvents.some(e => e.id === this.selectedEventId)
        if (!eventStillValid) {
          this.selectedEventId = undefined
          this.attendanceData = null
        }
      }
    },
    handleEventChange() {
      if (this.selectedEventId && this.selectedDate) {
        this.filterStatus = 'all'
        this.fetchAttendance()
      }
    },
    handleDateChange() {
      if (this.selectedEventId && this.selectedDate) {
        this.filterStatus = 'all'
        this.fetchAttendance()
      }
    },
    handleFilterChange(status) {
      this.filterStatus = status
      this.fetchAttendance()
    },
    getRequestParams() {
      let hostelId = this.selectedHostel
      if (!hostelId) {
        const selectedEvent = this.events.find(e => e.id === this.selectedEventId)
        if (selectedEvent) {
          hostelId = selectedEvent.hostel_id
        }
      }
      const params = {
        hostel_event_id: this.selectedEventId,
        date: moment(this.selectedDate).format('YYYY-MM-DD'),
      }
      if (hostelId) {
        params.hostel_id = hostelId
      }
      if (this.filterStatus && this.filterStatus !== 'all') {
        params.filter_status = this.filterStatus
      }
      return params
    },
    fetchAttendance() {
      if (!this.selectedEventId || !this.selectedDate) {
        this.$message.warning('Please select an event and a date.')
        return
      }
      this.loading = true
      this.$http.get('/api/hostel/attendance', {
        params: this.getRequestParams(),
      })
        .then(({ data }) => {
          this.attendanceData = data
        })
        .catch(error => {
          this.$neoNotify.error(error)
        })
        .finally(() => {
          this.loading = false
        })
    },

    handleApprove() {
      this.approveLoading = true
      this.$http.post('/api/hostel/attendance/approve', {
        hostel_event_id: this.selectedEventId,
        date: moment(this.selectedDate).format('YYYY-MM-DD'),
      })
        .then(({ data }) => {
          this.$message.success(data.message || 'Attendance approved.')
          this.fetchAttendance()
        })
        .catch(error => {
          this.$neoNotify.error(error)
        })
        .finally(() => {
          this.approveLoading = false
        })
    },
  },
}
</script>
